export type FacetConfig<T> = {
  mode: 'single' | 'multi';
  accessor: (item: T) => string;
};

export type FacetSelection<K extends string> = Partial<
  Record<K, string | string[]>
>;

export const removeFacetFromSelection = <K extends string>(
  selection: FacetSelection<K>,
  ...facets: K[]
): FacetSelection<K> => {
  const next: FacetSelection<K> = {};

  for (const key of Object.keys(selection) as K[]) {
    if (!facets.includes(key)) next[key] = selection[key];
  }

  return next;
};

export const applyFacetFilters = <T, K extends string>(
  items: T[],
  selection: FacetSelection<K>,
  config: Record<K, FacetConfig<T>>,
  opts?: { skipFacet?: K },
): T[] => {
  const facets = (Object.keys(config) as K[]).filter(
    (facet) => facet !== opts?.skipFacet,
  );

  return items.filter((item) =>
    facets.every((facet) => {
      const selected = selection[facet];

      if (selected === undefined) return true;

      const value = config[facet].accessor(item);

      // match multi value facets
      if (Array.isArray(selected)) {
        if (selected.length === 0) return true;
        return selected.includes(value);
      }

      // match single value facets
      return selected === value;
    }),
  );
};

export const computeFacetCounts = <T, K extends string>(
  items: T[],
  selection: FacetSelection<K>,
  config: Record<K, FacetConfig<T>>,
): Record<K, Record<string, number>> => {
  const facets = Object.keys(config) as K[];
  const result = {} as Record<K, Record<string, number>>;

  for (const facet of facets) {
    const counts: Record<string, number> = {};

    for (const item of items) {
      counts[config[facet].accessor(item)] = 0;
    }

    const filtered = applyFacetFilters(items, selection, config, {
      skipFacet: facet,
    });

    for (const item of filtered) {
      const value = config[facet].accessor(item);
      counts[value] = (counts[value] ?? 0) + 1;
    }

    result[facet] = counts;
  }

  return result;
};
